import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  BookOpen, Plus, Trash2, Edit2, Check, X, Loader2, Sparkles, AlertTriangle, GitCompare,
} from "lucide-react";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import HybridResearchPanel from "@/components/HybridResearchPanel";
import { DoctrineDiffViewer, DoctrineTestButton } from "@/pages/saas/V15Components";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import FoundingDoctrinePanel from "@/components/FoundingDoctrinePanel";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

const EMPTY_FORM = {
  title: "",
  description: "",
  principles: [""],
  filters: [""],
  priorities: [""],
  tone: "",
};

export default function Doctrine() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // "new" | doctrine object

  const load = async () => {
    try {
      const r = await api.get("/workspace/doctrines");
      setItems(r.data.items || []);
      setActiveId(r.data.active_id || null);
    } catch (e) {
      setError(fmtError(e));
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const activate = async (id) => {
    try {
      await api.post(`/workspace/doctrines/${id}/activate`);
      notify.success(t("saas.doctrine.toast_activated"));
      window.dispatchEvent(new Event("noam-doctrine-changed"));
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const remove = async (d) => {
    if (!window.confirm(t("saas.doctrine.confirm_delete"))) return;
    try {
      await api.delete(`/workspace/doctrines/${d.id}`);
      notify.success(t("saas.doctrine.toast_deleted"));
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="doctrine-root">
      <ThreeQPageHeader endpoint="/workspace/doctrine/top-priority" />
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#7F77DD] noam-overline">
            <BookOpen className="w-3 h-3" /> {t("saas.doctrine.overline")}
          </div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">{t("saas.doctrine.title")}</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
            {t("saas.doctrine.subtitle")}
          </p>
        </div>
        <button onClick={() => setEditing("new")} className="btn-primary text-xs" data-testid="doctrine-create-btn">
          <Plus className="w-3.5 h-3.5" /> {t("saas.doctrine.modal_new")}
        </button>
      </header>
      <div className="flex justify-end -mt-2">
        <ModuleHelpButton moduleKey="doctrine" />
      </div>

      {/* V23.15.24 — Doctrine 50 : moteur fondateur opaque + appropriation Ultra Admin */}
      <FoundingDoctrinePanel
        doctrines={items || []}
        onMarkedFoundational={load}
      />

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
          <span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}

      {items === null && (
        <div className="noam-card p-10 text-center text-zinc-400 text-sm">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      )}

      {items && items.length === 0 && (
        <div className="noam-card p-10 text-center" data-testid="doctrine-empty">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[#7F77DD]/10 border border-[#7F77DD]/30 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-[#7F77DD]" />
          </div>
          <h2 className="noam-display text-xl font-semibold mt-4">{t("saas.doctrine.empty_title")}</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            {t("saas.doctrine.empty_body")}
          </p>
          <button onClick={() => setEditing("new")} className="btn-primary text-sm mt-6 mx-auto inline-flex" data-testid="doctrine-empty-create">
            <Plus className="w-4 h-4" /> {t("saas.doctrine.create_first")}
          </button>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="doctrine-list">
          {items.map((d) => (
            <DoctrineCard
              key={d.id}
              doctrine={d}
              isActive={d.id === activeId}
              onActivate={() => activate(d.id)}
              onEdit={() => setEditing(d)}
              onDelete={() => remove(d)}
            />
          ))}
        </div>
      )}

      {/* P2 — Hybrid web research : vérifier la conformité réglementaire d'un principe */}
      {items && items.length > 0 && (
        <HybridResearchPanel
          contextModule="doctrine"
          defaultMode="hybrid"
          title="Vérifier conformité ou actualité réglementaire"
          testid="doctrine-hybrid"
          initialTopic=""
        />
      )}

      {editing && (
        <DoctrineEditor
          doctrine={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function DoctrineCard({ doctrine, isActive, onActivate, onEdit, onDelete }) {
  return (
    <div
      className={`noam-card p-5 ${isActive ? "ring-1 ring-[#7F77DD]/40" : ""}`}
      data-testid={`doctrine-card-${doctrine.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="noam-display text-lg font-semibold">{doctrine.title}</h3>
            {isActive && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#7F77DD]/20 text-[#7F77DD] border border-[#7F77DD]/40 font-medium uppercase tracking-wider">
                Active
              </span>
            )}
          </div>
          {doctrine.description && (
            <p className="text-zinc-400 text-xs mt-1">{doctrine.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isActive && (
            <DiffTrigger doctrineId={doctrine.id} />
          )}
          {!isActive && (
            <button onClick={onActivate} className="text-[#7F77DD] hover:bg-white/5 p-1.5 rounded" title="Activer cette doctrine" data-testid={`doctrine-activate-${doctrine.id}`}>
              <Sparkles className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onEdit} className="text-zinc-400 hover:text-white p-1.5 rounded hover:bg-white/5" title="Modifier" data-testid={`doctrine-edit-${doctrine.id}`}>
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} className="text-zinc-500 hover:text-[#E24B4A] p-1.5 rounded hover:bg-white/5" title="Supprimer" data-testid={`doctrine-delete-${doctrine.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <Section label="Principes" items={doctrine.principles} color="#1D9E75" />
      <Section label="Filtres de décision" items={doctrine.filters} color="#378ADD" />
      <Section label="Priorités" items={doctrine.priorities} color="#EF9F27" />
      {doctrine.tone && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Ton attendu</div>
          <div className="text-xs text-zinc-300 italic">« {doctrine.tone} »</div>
        </div>
      )}
    </div>
  );
}

function Section({ label, items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-xs text-zinc-200 flex items-start gap-2">
            <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: color }} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DoctrineEditor({ doctrine, onClose, onSaved }) {
  const isEdit = !!doctrine;
  const [form, setForm] = useState(() => doctrine ? {
    title: doctrine.title || "",
    description: doctrine.description || "",
    principles: doctrine.principles?.length ? doctrine.principles : [""],
    filters: doctrine.filters?.length ? doctrine.filters : [""],
    priorities: doctrine.priorities?.length ? doctrine.priorities : [""],
    tone: doctrine.tone || "",
  } : { ...EMPTY_FORM });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateList = (key, idx, v) => setForm((f) => {
    const arr = [...f[key]];
    arr[idx] = v;
    return { ...f, [key]: arr };
  });
  const addItem = (key) => setForm((f) => ({ ...f, [key]: [...f[key], ""] }));
  const removeItem = (key, idx) => setForm((f) => {
    const arr = f[key].filter((_, i) => i !== idx);
    return { ...f, [key]: arr.length ? arr : [""] };
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        principles: form.principles.map((x) => x.trim()).filter(Boolean),
        filters: form.filters.map((x) => x.trim()).filter(Boolean),
        priorities: form.priorities.map((x) => x.trim()).filter(Boolean),
        tone: form.tone.trim(),
      };
      if (isEdit) {
        await api.patch(`/workspace/doctrines/${doctrine.id}`, payload);
        notify.success("Doctrine mise à jour.");
      } else {
        await api.post("/workspace/doctrines", payload);
        notify.success("Doctrine créée.");
      }
      onSaved();
    } catch (e) { setErr(fmtError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="doctrine-editor-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline">{isEdit ? "Modifier la doctrine" : "Nouvelle doctrine"}</div>
            <div className="noam-display text-base font-semibold mt-0.5">{isEdit ? doctrine.title : "Doctrine de décision"}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="overflow-y-auto p-5 space-y-4">
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Titre *</label>
            <input required autoFocus value={form.title} onChange={(e) => updateField("title", e.target.value)}
              placeholder="Ex : Doctrine du décideur Constante"
              className="noam-input" data-testid="doctrine-form-title" />
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Description</label>
            <textarea value={form.description} onChange={(e) => updateField("description", e.target.value)} rows={2}
              placeholder="En une phrase, à quoi sert cette doctrine."
              className="noam-input" data-testid="doctrine-form-description" />
          </div>

          <ListField label="Principes" items={form.principles} fieldKey="principles" color="#1D9E75" ph="Ex : Captation de valeur en amont"
            onChange={updateList} onAdd={addItem} onRemove={removeItem} />
          <ListField label="Filtres de décision" items={form.filters} fieldKey="filters" color="#378ADD" ph="Ex : Solvable · Monétisable · Voulu réellement"
            onChange={updateList} onAdd={addItem} onRemove={removeItem} />
          <ListField label="Priorités" items={form.priorities} fieldKey="priorities" color="#EF9F27" ph="Ex : Coût d'inaction > urgence apparente"
            onChange={updateList} onAdd={addItem} onRemove={removeItem} />

          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Ton attendu (optionnel)</label>
            <input value={form.tone} onChange={(e) => updateField("tone", e.target.value)}
              placeholder="Ex : concis, ferme, factuel" className="noam-input" data-testid="doctrine-form-tone" />
          </div>

          {err && (
            <div className="flex items-start gap-2 p-2.5 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {err}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5 sticky bottom-0 bg-[var(--noam-bg)]">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">Annuler</button>
            <button type="submit" disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="doctrine-form-submit">
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…</> : <><Check className="w-3.5 h-3.5" /> {isEdit ? "Enregistrer" : "Créer"}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ListField({ label, items, fieldKey, color, ph, onChange, onAdd, onRemove }) {
  return (
    <div>
      <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">{label}</label>
      <div className="space-y-1.5">
        {items.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
            <input
              value={v}
              onChange={(e) => onChange(fieldKey, i, e.target.value)}
              placeholder={ph}
              className="noam-input flex-1"
              data-testid={`doctrine-${fieldKey}-${i}`}
            />
            {items.length > 1 && (
              <button type="button" onClick={() => onRemove(fieldKey, i)}
                className="text-zinc-500 hover:text-[#E24B4A] p-1.5 rounded hover:bg-white/5" title="Supprimer">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        <button type="button" onClick={() => onAdd(fieldKey)}
          className="text-[11px] text-[#378ADD] hover:text-white flex items-center gap-1 mt-1" data-testid={`doctrine-${fieldKey}-add`}>
          <Plus className="w-3 h-3" /> Ajouter
        </button>
      </div>
    </div>
  );
}



// Vague 15 — Trigger pour la modal de diff entre 2 révisions
function DiffTrigger({ doctrineId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[#7F77DD] hover:bg-white/5 p-1.5 rounded"
        title="Comparer deux révisions"
        data-testid={`doctrine-diff-${doctrineId}`}
      >
        <GitCompare className="w-3.5 h-3.5" />
      </button>
      {open && <DoctrineDiffViewer doctrineId={doctrineId} onClose={() => setOpen(false)} />}
    </>
  );
}
